/**
 * Member color palette — pulled from the existing event-kind tints so
 * the calendar reads as ONE coherent system instead of two separate
 * color languages.
 *
 * The 12 colors are picked from `EVENT_KINDS` plus a few extra
 * pastels at the same saturation level so adjacent members never get
 * accidentally identical tints. Order matters: this is the
 * auto-assign sequence for new family members. A 13th member
 * recycles from index 0.
 *
 * Each entry has a `key` (stable, used in storage) and a `hex` (used
 * for rendering). Don't reorder keys without a migration — existing
 * member docs persist the key.
 */

export const MEMBER_COLORS = [
  { key: "sage", label: "Sage", hex: "#C9F0CB" },          // matches activity tint
  { key: "peach", label: "Peach", hex: "#FFB0A0" },         // matches dinner tint
  { key: "pink", label: "Pink", hex: "#E8C8DD" },           // matches social tint
  { key: "lavender", label: "Lavender", hex: "#ECD5F5" },   // matches event tint
  { key: "apricot", label: "Apricot", hex: "#FFC9A8" },     // matches day-trip tint
  { key: "butter", label: "Butter", hex: "#FFEAA0" },       // matches school-event tint
  { key: "cream", label: "Cream", hex: "#FFE0BC" },         // matches kids tint
  { key: "sky", label: "Sky", hex: "#CFE2F5" },             // matches travel tint
  { key: "mint", label: "Mint", hex: "#CCEDE3" },           // matches appointment tint
  { key: "slate", label: "Slate", hex: "#D8E3EB" },         // matches work tint
  { key: "cherry", label: "Cherry", hex: "#FFD2DD" },       // matches birthday tint
  { key: "sand", label: "Sand", hex: "#EFE4D2" },           // matches "other" tint
];

const KEY_BY_HEX = new Map(MEMBER_COLORS.map((c) => [c.hex.toLowerCase(), c.key]));
const HEX_BY_KEY = new Map(MEMBER_COLORS.map((c) => [c.key, c.hex]));

/** Resolve a member doc's stored color to a hex. Prefers
 *  `accent_color`; falls back to the legacy `color` field for older
 *  member docs (mint/peach/lavender/butter/sky/cherry — all of which
 *  exist as keys in the new palette too, so the resolution is exact).
 *  Last resort: deterministic-by-id auto-assign so two siblings always
 *  get the same two colours regardless of page load. */
export function memberAccentHex(member, fallbackIndex = 0) {
  if (!member) return MEMBER_COLORS[0].hex;
  for (const candidate of [member.accent_color, member.color]) {
    const stored = (candidate || "").toString().trim().toLowerCase();
    if (!stored) continue;
    if (HEX_BY_KEY.has(stored)) return HEX_BY_KEY.get(stored);
    if (stored.startsWith("#") && stored.length >= 4) return stored;
  }
  const seed = (member.id || member.name || "");
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const idx = hash % MEMBER_COLORS.length;
  return MEMBER_COLORS[(idx + fallbackIndex) % MEMBER_COLORS.length].hex;
}

/** Next free color key in the palette, given the family's current set
 *  of accent_color values. Used on member create so a brand-new
 *  member gets a colour that's not already in use. Cycles back to
 *  the start once all 12 are taken. */
export function nextFreeAccentKey(existingMembers = []) {
  const taken = new Set(
    existingMembers
      .map((m) => (m.accent_color || "").trim().toLowerCase())
      .filter(Boolean),
  );
  for (const c of MEMBER_COLORS) {
    if (!taken.has(c.key)) return c.key;
  }
  return MEMBER_COLORS[0].key;
}

/** Tiny helper — given a hex string, return the matching palette key
 *  (or null if it doesn't belong to the palette). */
export function paletteKeyForHex(hex) {
  if (!hex) return null;
  return KEY_BY_HEX.get(hex.toLowerCase()) || null;
}
